/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// annotation-layer.js — labels & notes on the desk (Feature 120), rendered in
// the dedicated `.layer-annotations` between the wires and the interaction
// overlay (above wires, below ghosts). One renderer rebuilds only on doc
// change or an annotation drag — never on pan/zoom (transform-only, the same
// discipline as WireLayer). Each annotation is one absolutely-positioned DOM
// box in world px; empty space between them is the zero-size layer anchor, so
// the bare desk still falls through to the viewport.
//
// Pure decoration: annotations are ignored by occupancy, the netlist, and the
// engine. Position (x/y, world pitch units) is absolute; an `anchor` (a
// component id) makes it ride that part's drag via render()'s `shift` — which
// names a SET of anchors, since a selection dragged as one carries every label
// hung on any of its members.

import { clear, el } from "../dom.js";
import { PX_PER_UNIT } from "../desk/desk-geometry.js";
import { t } from "../i18n.js";

export class AnnotationLayer {
  #layer;
  #doc;
  #onPointerDown;
  #onContextMenu;
  #onEditCommit;
  #els = new Map(); // annotationId → element
  #selectedId = null;
  #editing = null; // annotationId while an inline editor is open
  #cancelEdit = null; // closes the open editor without committing

  /**
   * @param {HTMLElement} layer - the `.layer-annotations` element.
   * @param {import('../model/desk-doc.js').DeskDoc} deskDoc
   * @param {object} [callbacks]
   * @param {(id: string, e: PointerEvent) => void} [callbacks.onPointerDown]
   * @param {(id: string, e: MouseEvent) => void} [callbacks.onContextMenu]
   * @param {(id: string, text: string) => void} [callbacks.onEditCommit]
   */
  constructor(
    layer,
    deskDoc,
    { onPointerDown, onContextMenu, onEditCommit } = {},
  ) {
    this.#layer = layer;
    this.#doc = deskDoc;
    this.#onPointerDown = onPointerDown;
    this.#onContextMenu = onContextMenu;
    this.#onEditCommit = onEditCommit;

    window.addEventListener("chiphippo:doc-changed", () => this.render());
    // Another document on the desk (a desktop switch, a project opened from
    // the menu, with the editor still focused): the text in hand belongs to
    // the LAST one, and committing it on the blur that follows would write
    // it into whichever annotation of this one has the same id.
    window.addEventListener("chiphippo:desk-loaded", () =>
      this.#cancelEdit?.(),
    );
    // An empty annotation's placeholder is in the language in force.
    window.addEventListener("chiphippo:locale-changed", () => this.render());
    this.render();
  }

  /**
   * Rebuild every annotation from the document. `shift` (an anchored-drag
   * override) nudges only the annotations anchored to one of `anchorIds` by
   * (dx, dy) world pitch units, so a label rides its chip live. Skipped while an
   * inline editor is open, so a mid-edit doc-changed can't tear the editor down.
   *
   * A SET rather than one id, because a whole selection dragged together moves
   * by one delta and may carry several labels — the same shape
   * `DeskController#shiftAnchoredAnnotations` already commits with.
   * @param {{anchorIds: Set<string>, dx: number, dy: number}|null} [shift]
   */
  render(shift = null) {
    if (this.#editing) return;
    clear(this.#layer);
    this.#els.clear();
    for (const ann of this.#doc.annotations) {
      const box = this.#build(ann, shift);
      this.#layer.append(box);
      this.#els.set(ann.id, box);
    }
  }

  #build(ann, shift) {
    let x = ann.x;
    let y = ann.y;
    if (shift && ann.anchor && shift.anchorIds.has(ann.anchor)) {
      x += shift.dx;
      y += shift.dy;
    }
    const box = el("div", {
      class: `annotation annotation--${ann.kind}`,
      dataset: { annId: ann.id },
    });
    if (ann.anchor) box.classList.add("annotation--anchored");
    if (ann.color) box.style.setProperty("--annotation-color", ann.color);
    box.style.left = `${x * PX_PER_UNIT}px`;
    box.style.top = `${y * PX_PER_UNIT}px`;
    box.classList.toggle("annotation--selected", ann.id === this.#selectedId);

    const text = el("div", { class: "annotation-text" });
    if (ann.text) {
      text.textContent = ann.text;
    } else {
      text.classList.add("annotation-text--empty");
      text.textContent = t(
        ann.kind === "note"
          ? "palette.annotation.note"
          : "palette.annotation.label",
      );
    }
    box.append(text);

    box.addEventListener("pointerdown", (e) =>
      this.#onPointerDown?.(ann.id, e),
    );
    box.addEventListener("contextmenu", (e) =>
      this.#onContextMenu?.(ann.id, e),
    );
    box.addEventListener("dblclick", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.beginEdit(ann.id);
    });
    return box;
  }

  /** Highlight one annotation (null clears). Survives re-renders. */
  setSelected(id) {
    this.#selectedId = id;
    for (const [annId, box] of this.#els) {
      box.classList.toggle("annotation--selected", annId === id);
    }
  }

  /** Live-reposition one annotation's box (world pitch units) during a drag. */
  setPosition(id, x, y) {
    const box = this.#els.get(id);
    if (!box) return;
    box.style.left = `${x * PX_PER_UNIT}px`;
    box.style.top = `${y * PX_PER_UNIT}px`;
  }

  /** True while an inline editor is open (input/textarea has focus). */
  get editing() {
    return this.#editing !== null;
  }

  /**
   * Replace an annotation's text with an inline editor (a one-line input for a
   * label, a textarea for a note). Enter or blur commits; Escape cancels.
   * Commit routes through `onEditCommit` so the document is the source of truth
   * and the re-render redraws from it.
   */
  beginEdit(id) {
    if (this.#editing) return;
    const ann = this.#doc.getAnnotation(id);
    const box = this.#els.get(id);
    if (!ann || !box) return;
    this.#editing = id;
    this.#selectedId = id;

    const editor = el(ann.kind === "note" ? "textarea" : "input", {
      class: "annotation-editor",
      value: ann.text ?? "",
    });
    clear(box);
    box.classList.add("annotation--editing");
    box.append(editor);
    editor.focus();
    editor.select?.();

    let done = false;
    const finish = (commit) => {
      if (done) return;
      done = true;
      const text = editor.value;
      this.#editing = null;
      this.#cancelEdit = null;
      box.classList.remove("annotation--editing");
      if (commit) {
        this.#onEditCommit?.(id, text); // emits doc-changed → full re-render
      } else {
        this.render(); // cancel: redraw from the document as it stood
      }
    };
    this.#cancelEdit = () => finish(false);
    editor.addEventListener("blur", () => finish(true));
    editor.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        finish(false);
      } else if (e.key === "Enter" && (ann.kind !== "note" || e.metaKey)) {
        // Enter commits a label; a note keeps Enter for newlines (⌘Enter ends).
        e.preventDefault();
        finish(true);
      }
    });
  }
}
